import { useMemo, useState } from "react";
import { PackageOpen } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import PageHero from "@/components/bakery/PageHero";
import { CrateArt } from "@/components/bakery/Art";
import { FormShell, useFormReset } from "@/components/forms/ProductionForms";
import LinesEditor, { cleanLines } from "@/components/common/LinesEditor";
import PhotoUploader from "@/components/common/PhotoUploader";
import PicSelect from "@/components/common/PicSelect";
import TxnDetailDialog from "@/components/common/TxnDetailDialog";
import { VoidBadge } from "@/components/common/Chips";
import { DateField, Field, inputCls } from "@/components/common/Field";
import { EmptyState, Loading, PrimaryBtn } from "@/components/common/ui";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { fmtDate, shiftDate, todayJkt } from "@/lib/format";
import { cn } from "@/lib/utils";

const hint = (p, q) => ((p.purchase_pack_size || 1) > 1 ? `${q} pack × ${p.purchase_pack_size} = +${q * p.purchase_pack_size} ${p.stock_unit}` : `+${q} ${p.stock_unit}`);

function PurchaseForm({ open, onClose, products, onSaved }) {
  const f = useFormReset(open, todayJkt());
  const [supplier, setSupplier] = useState("");
  const [ref, setRef] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const sorted = useMemo(() => [...(products || [])].sort((a, b) => (b.purchase_pack_size > 1) - (a.purchase_pack_size > 1)), [products]);
  const submit = async () => {
    const lines = cleanLines(f.lines).map(({ product_id, qty }) => ({ product_id, qty }));
    if (!lines.length) return toast.error("Isi minimal 1 produk");
    setSaving(true);
    try {
      const { data } = await api.post("/purchase", { date: f.date, supplier, ref_no: ref, pic_user_id: f.pic, notes: f.notes, lines, photo_ids: photos.map((x) => x.id) });
      toast.success(`Barang masuk tercatat · ${data.txn_no}`);
      setSupplier(""); setRef(""); setPhotos([]);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <FormShell open={open} onClose={onClose} title="Terima Barang" desc="Qty dalam pack supplier. Tortilla otomatis dikonversi: 1 pack = 10 lembar." testid="purchase-form">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <DateField value={f.date} onChange={f.setDate} testid="purchase-date" />
          <Field label="No. Surat Jalan"><input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Opsional" className={inputCls} data-testid="purchase-ref" /></Field>
        </div>
        <Field label="Nama supplier" required><input value={supplier} onChange={(e) => setSupplier(e.target.value)} className={inputCls} data-testid="purchase-supplier" /></Field>
        <PicSelect value={f.pic} onChange={f.setPic} testid="purchase-pic" />
        <Field label="Barang diterima" required>
          <LinesEditor lines={f.lines} setLines={f.setLines} products={sorted} filter={(p) => !p.is_bundle && p.category !== "Frozen Dough"} unitHint={hint} testid="purchase-lines" />
        </Field>
        <Field label="Foto surat jalan (opsional)"><PhotoUploader value={photos} onChange={setPhotos} max={3} testid="purchase-photos" /></Field>
        <Field label="Catatan"><Textarea value={f.notes} onChange={(e) => f.setNotes(e.target.value)} data-testid="purchase-notes" /></Field>
        <PrimaryBtn className="w-full" onClick={submit} disabled={saving || supplier.trim().length < 2 || !f.pic} data-testid="purchase-submit">{saving ? "Menyimpan…" : "Simpan ke Gudang"}</PrimaryBtn>
      </div>
    </FormShell>
  );
}

function CrateCard({ t, onOpen }) {
  return (
    <button onClick={() => onOpen(t.id)} data-testid={`crate-card-${t.txn_no}`}
      className={cn("lift press relative overflow-hidden rounded-xl border-2 border-ink bg-white text-left", t.status === "void" && "opacity-60")}>
      <div className="h-3 bg-[repeating-linear-gradient(90deg,#0A0A0A_0_14px,transparent_14px_22px)]" />
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div><div className="font-display text-lg font-bold">{t.supplier}</div><div className="num text-[11px] text-zinc-400">{t.txn_no} · {fmtDate(t.date)}{t.ref_no ? ` · SJ ${t.ref_no}` : ""}</div></div>
          <VoidBadge txn={t} />
        </div>
        <ul className="mt-3 space-y-1.5 border-t border-dashed border-zinc-300 pt-3 text-sm">
          {t.lines.map((l, i) => (
            <li key={i} className="flex justify-between gap-2"><span className="truncate">{l.product_name}</span>
              <span className="num shrink-0 font-semibold">{l.qty}{l.purchase_pack_size > 1 ? ` pack → +${l.qty * l.purchase_pack_size} ${l.stock_unit}` : ` ${l.stock_unit}`}</span></li>
          ))}
        </ul>
        <div className="mt-3 text-[11px] text-zinc-500">PIC {t.pic_name}{t.photos?.length ? ` · ${t.photos.length} foto` : ""}</div>
      </div>
    </button>
  );
}

export default function GudangMasuk() {
  const [from, setFrom] = useState(shiftDate(todayJkt(), -29));
  const [to, setTo] = useState(todayJkt());
  const { data: txns, loading, reload } = useApi("/transactions", { type: "PUR", date_from: from, date_to: to });
  const { data: products, reload: reloadP } = useApi("/products");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  return (
    <div data-testid="gudang-page">
      <PageHero testid="gudang-hero" eyebrow="Transaksi · Purchase" title="Gudang Masuk" Art={CrateArt}
        desc="Barang jadi dari supplier (tortilla & lainnya). Tanpa harga — cukup qty pack, surat jalan, dan PIC."
        actions={<PrimaryBtn onClick={() => setOpen(true)} data-testid="open-purchase-form"><PackageOpen className="h-4 w-4" /> Terima Barang</PrimaryBtn>} />
      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={cn(inputCls, "sm:w-48")} data-testid="purchase-filter-from" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={cn(inputCls, "sm:w-48")} data-testid="purchase-filter-to" />
      </div>
      {loading && !txns ? <Loading /> : txns?.length ? (
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="purchase-list">{txns.map((t) => <CrateCard key={t.id} t={t} onOpen={setDetail} />)}</div>
      ) : <EmptyState Art={CrateArt} title="Gudang belum ada kiriman" desc="Belum ada barang masuk di periode ini." testid="purchase-empty" />}
      <PurchaseForm open={open} onClose={() => setOpen(false)} products={products} onSaved={() => { setOpen(false); reload(); reloadP(); }} />
      <TxnDetailDialog txnId={detail} onClose={() => setDetail(null)} onChanged={reload} />
    </div>
  );
}
